import Menu, { MenuItem } from 'rc-menu';
import { Component, ReactElement } from 'react';
import { menuBuiltinPlacements } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DownloadButton } from '../../../../../../shared/frontend/components/downloadButton';
import history, { handleRCMenuOnClick, navigateTo } from '../../../../../../shared/frontend/libs/history';
import { DIFFICULTIES } from '../../../../shared/enums';
import { RouteComponentProps } from '../../../../shared/types/common';
import withQueueModal from '../../../HOCS/withQueueModal';
import { resolveRoutePath } from '../../bundles/routes';
import { TSiteMetaClientState } from '../../libs/store';
import { specsSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import { Media } from '../../../../../../shared/frontend/components/presentational/media';
import { buildMenus, buildRouteParams } from './menuHelper';
import { getMenuItems } from './menuItems';
import { globalSearchActions } from '../../../../../../shared/frontend/scripts/global-search/store/modules';

type TPropsFromState = TSiteMetaClientState & {
  zone?: string;
  combinedRegions?: any[];
  isDungeon?: boolean;
  isRaid?: boolean;
  isCharacter?: boolean;
  isTournament?: boolean;
  showQueueModal: () => void;
};

export type TDesktopSiteNavProps = TPropsFromState & RouteComponentProps<any, any>;

function onClickMenu(props: any, params: any) {
  const { key } = params;
  switch (key) {
    case 'update_now':
      props.showQueueModal();
      break;
    default:
      handleRCMenuOnClick(params);
      break;
  }
}

/**
 * DesktopSiteNavContentLarge
 */
const DesktopSiteNavContentLarge = withTranslation()(
  withQueueModal((props: TDesktopSiteNavProps) => {
    const { t } = props;
    return (
      <div className="rio-hidden--small rio-hidden--medium rio-desktop-nav">
        <div className="rio-tabs--scoped slds-tabs--scoped">
          <div className="slds-grid rio-title-container">
            <div className="slds-size--9-of-12">
              <Menu
                selectable={false}
                mode="horizontal"
                builtinPlacements={menuBuiltinPlacements}
                className="rio-title-menu"
                onClick={onClickMenu.bind(null, props)}
              >
                {buildMenus('menus', getMenuItems(props))}
                {!props.currentFeatures.premium && (
                  <MenuItem key="https://www.patreon.com/join/RaiderIO?utm_source=raiderio&utm_campaign=nav">
                    <div className="text-bold text-premium-special text-premium-special--animated">
                      {t('nav:main.go-premium')}
                    </div>
                  </MenuItem>
                )}
              </Menu>
            </div>
            <div className="slds-col slds-text-align--left">
              <DownloadButton
                onClick={(ev: any) => {
                  RIO_TRACK_EVENT('event', 'select_content', {
                    content_type: 'view_addon_page'
                  });
                  navigateTo('https://raider.io/addon', ev);
                }}
                style={{
                  position: 'relative',
                  top: 4
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  })
);

class DesktopSiteNavContentSmallBase extends Component<TDesktopSiteNavProps, {}> {
  public updateFilter(name: string, event: any) {
    const newRouteParams = buildRouteParams(this.props.match.params, { [name]: event.target.value });
    const path = resolveRoutePath(this.props.match.path, newRouteParams);
    history.push(path);
  }

  public render(): ReactElement<TDesktopSiteNavProps> {
    if (!this.props.match.params.raid || !this.props.match.params.raid.length) {
      return <div />;
    }

    return (
      <div className="slds-context-bar">
        <nav
          className="slds-context-bar__primary slds-form--inline rio-context-bar slds-align--absolute-center"
          role="navigation"
        >
          <label className="slds-form-element__label text-muted" htmlFor="filter-region">
            Region:
          </label>
          &nbsp;
          <div className="slds-form-element">
            <div className="slds-form-element__control">
              <div className="slds-select_container">
                <select
                  id="filter-region"
                  className="slds-select"
                  onChange={this.updateFilter.bind(this, 'region')}
                  defaultValue={this.props.match.params.region}
                >
                  {this.props.regions
                    .filter((region: any) => region.slug !== 'cn' || this.props.isTournament)
                    .map((region: any, index: number) => (
                      <option key={index} value={region.slug}>
                        {region.short_name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
          <label className="slds-form-element__label text-muted rio-filter-margin" htmlFor="filter-difficulty">
            Difficulty:
          </label>
          &nbsp;
          <div className="slds-form-element">
            <div className="slds-form-element__control">
              <div className="slds-select_container">
                <select
                  id="filter-difficulty"
                  className="slds-select"
                  onChange={this.updateFilter.bind(this, 'difficulty')}
                  defaultValue={this.props.match.params.difficulty}
                >
                  {DIFFICULTIES.map((difficulty: any, index: number) => (
                    <option key={index} value={difficulty.slug}>
                      {difficulty.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </nav>
      </div>
    );
  }
}

const DesktopSiteNavContentSmall = withTranslation()(DesktopSiteNavContentSmallBase);

function DesktopSiteNavBase(props: TDesktopSiteNavProps) {
  const computedProps = {
    combinedRegions: [...props.regions, ...(props.subRegions || [])],
    zone: props.match.params.raid || props.match.params.dungeon,
    isDungeon: !!props.match.params.dungeon,
    isRaid: !!props.match.params.raid,
    isCharacter: !!props.match.params.class,
    isTournament: ([] as string[]).indexOf(props.match.path) !== -1
  };

  return (
    <>
      <Media lessThan="lg">
        <DesktopSiteNavContentSmall {...props} {...computedProps} />
      </Media>
      <Media greaterThanOrEqual="lg">
        <DesktopSiteNavContentLarge {...props} {...computedProps} />
      </Media>
    </>
  );
}

// take state from store and convert into props
function mapStateToProps(state: any, ownProps: TDesktopSiteNavProps): TPropsFromState {
  return {
    currentFeatures: state.currentFeatures,
    ...ownProps,
    ...state.siteMeta,
    specs: specsSelector(state)
  };
}

const mapDispatchToProps = {
  resetPagination: globalSearchActions.resetPagination
};

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect<TPropsFromState, TDesktopSiteNavProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(DesktopSiteNavBase);
